<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import instagramClient from 'dashboard/api/channel/instagramClient';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const isReauthorizing = ref(false);
const enabled = ref(false);
const lastSyncedAt = ref('');
const lastSyncError = ref('');

async function fetchConfig() {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getInstagramMediaMonitoringConfig(
      props.inbox.id
    );
    if (data.success && data.config) {
      enabled.value = data.config.enabled || false;
      lastSyncedAt.value = data.config.last_synced_at || '';
      lastSyncError.value = data.config.last_sync_error || '';
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function saveConfig() {
  isSaving.value = true;
  try {
    const { data } = await InboxesAPI.updateInstagramMediaMonitoringConfig(
      props.inbox.id,
      { enabled: enabled.value }
    );
    if (data.success) {
      useAlert(t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.SAVE_SUCCESS'));
    } else {
      useAlert(
        data.error || t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.SAVE_ERROR')
      );
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

async function requestReauthorization() {
  try {
    isReauthorizing.value = true;
    const response = await instagramClient.generateAuthorization();
    const {
      data: { url },
    } = response;
    window.location.href = url;
  } catch {
    useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH'));
  } finally {
    isReauthorizing.value = false;
  }
}

onMounted(() => {
  fetchConfig();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.SUBTITLE')"
    >
      <div v-if="isLoading" class="p-4 text-center">
        <span>{{ $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.LOADING') }}</span>
      </div>

      <div v-else class="space-y-6">
        <div class="flex items-center gap-3">
          <input
            id="media-monitoring-enabled"
            v-model="enabled"
            type="checkbox"
            class="w-4 h-4"
          />
          <label for="media-monitoring-enabled" class="font-medium">
            {{ $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.ENABLE_LABEL') }}
          </label>
        </div>

        <div v-if="lastSyncedAt" class="text-sm text-n-slate-11">
          <span class="font-medium">
            {{ $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.LAST_SYNCED') }}:
          </span>
          {{ lastSyncedAt }}
        </div>

        <div
          v-if="lastSyncError"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 p-3 rounded-lg"
        >
          <span class="font-medium">
            {{ $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.LAST_ERROR') }}:
          </span>
          {{ lastSyncError }}
        </div>

        <!-- Reauthorize for Insights -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-1 space-y-3">
          <p class="text-sm font-medium text-n-slate-12">
            {{
              $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.REAUTH_INSIGHTS_TITLE')
            }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.REAUTH_INSIGHTS_DESC')
            }}
          </p>
          <NextButton
            :is-loading="isReauthorizing"
            :label="
              $t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.REAUTH_INSIGHTS_BUTTON')
            "
            variant="secondary"
            @click="requestReauthorization"
          />
        </div>

        <div class="flex justify-end pt-4">
          <NextButton
            :is-loading="isSaving"
            :label="$t('INBOX_MGMT.INSTAGRAM_MEDIA_MONITORING.SAVE')"
            @click="saveConfig"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
